import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { Lesson } from "@edu/shared";
import { endLesson, getLesson } from "../../lib/api.ts";
import {
  callExitRunner,
  callSeenOf,
  cardCheckOf,
  endedAlready,
  goneText,
  markCallSeen,
  tabStore,
  type CallExitRunner,
  type CallRole,
  type CardCheck,
} from "./call-exit.ts";
import { ENTRY_TIMEOUT_MS } from "./entry-steps.ts";

/**
 * Уход из комнаты звонка с преподавателями (6.4 и 9.5
 * `docs/teacher-calls-design.md`): правила в `call-exit.ts`, исполнение с
 * памятью в `callExitRunner`, здесь только проводка к маршрутизатору и сети.
 *
 * ⚠️ **Модуль стартовый**: его берёт страница урока. Ни комнаты, ни
 * `livekit-client` здесь нет.
 *
 * ⚠️ **Перезапрос карточки со сроком** (`ENTRY_TIMEOUT_MS`), как у входа: без
 * срока запрос в режиме белого списка висел бы минутами, а пока он идёт,
 * прочие разрывы молчат (`callExitRunner`). Не дождались значит `failed`, то
 * есть «Уроки», откуда видно, что со связью.
 */
export function useCallExit(args: {
  lessonId: string;
  /** Моя роль в звонке, либо `null` у урока и пока карточки нет. */
  role: CallRole | null;
  /** Урок: прежний выход в расписание. */
  leaveLesson: () => void;
}): {
  screen: string | null;
  exit: CallExitRunner;
  /**
   * Карточка приехала: вкладка запоминает, что была в звонке. ДО входа в
   * комнату: после конца карточка ответит 404 обоим, и только отметка отличит
   * «звонок завершён» от «урок не найден» после перезагрузки (9.5 проекта).
   */
  entered: (l: Lesson) => void;
  /**
   * Карточка не приехала: `true`, если это конец звонка, в котором вкладка
   * была, и экран конца уже поставлен. Завершённый звонок лежит в корзине, и
   * сервер не различает «не было» и «было и кончилось» намеренно.
   */
  gone: (e: unknown) => boolean;
  /**
   * «Завершить» звонка. 404 это успех: звонок уже завершили из другой вкладки
   * ведущего, и он в корзине (9.5 проекта). Прочий отказ всплывает в кнопку.
   */
  end: () => Promise<void>;
} {
  const navigate = useNavigate();
  /**
   * Экран конца вместе с уроком, которому он принадлежит. Страница без `key`
   * при смене урока в адресе остаётся тем же экземпляром, и экран прежнего
   * урока сбрасывается при рендере, по паре, а не эффектом (эффект показал бы
   * кадр старого экрана).
   */
  const [ended, setEnded] = useState<{ lessonId: string; text: string } | null>(null);
  const screen = ended !== null && ended.lessonId === args.lessonId ? ended.text : null;
  const latest = useRef({ ...args, navigate });
  latest.current = { ...args, navigate };
  const lessonId = args.lessonId;

  const exit = useMemo(
    () =>
      callExitRunner(() => {
        const now = latest.current;
        return {
          role: now.role,
          leaveLesson: now.leaveLesson,
          toLessons: (replace) => void now.navigate("/lessons", { replace }),
          toScreen: (text) => setEnded({ lessonId, text }),
          checkCard: () =>
            getLesson(lessonId, { timeoutMs: ENTRY_TIMEOUT_MS }).then(
              (): CardCheck => "found",
              (e: unknown) => cardCheckOf(e),
            ),
        };
      }),
    [lessonId],
  );

  /**
   * Исполнитель живёт ровно страницу этого урока: размонтирование и смена урока
   * в адресе (страница без `key`, тот же экземпляр) гасят его.
   */
  useEffect(() => {
    exit.resume();
    return () => exit.dispose();
  }, [exit]);

  const extra = useMemo(
    () => ({
      entered: (l: Lesson) => {
        if (l.call !== undefined) markCallSeen(tabStore(), l.id, l.call.role);
      },
      gone: (e: unknown) => {
        const seen = callSeenOf(tabStore(), lessonId);
        if (seen === null || cardCheckOf(e) !== "gone") return false;
        exit.show(goneText(seen));
        return true;
      },
      end: async () => {
        try {
          await endLesson(lessonId);
        } catch (e) {
          if (!endedAlready(e)) throw e;
        }
        exit.ended();
      },
    }),
    [lessonId, exit],
  );

  return { screen, exit, ...extra };
}
